import React, { useState } from "react";
import { BotMessageSquare, Sparkles, Wand } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { format, addDays } from "date-fns";
import AIContentAction from "../../actions/AIContentAction";
import SelectField from "../../components/ui/SelectField";
import InputField from "../../components/ui/InputField";
import { Button, Modal } from "../../components/ui";
import PlatformSelection, { Platform } from "./PlatformSelection";
import ConfirmDialog from "../../dialog/ConfirmDialog";
import TriangleAlertIcon from "../../icons/TriangleAlertIcon";
import {
  selectProject,
  selectAiSettingPlain,
  selectHasValidAiSetting,
} from "../selectors/aiContentSelectors";
import { LANGUAGE_OPTIONS, TIMEZONE_OPTIONS } from "../constants/timezoneConstants";
import {
  defaultContentInstructions,
  resolveContentInstructions,
} from "../constants/contentInstructionsDefaults";
import { generateContentInstructions, resetAIAgent } from "../../api/AIContentApi";
import ChartLoading from "../../insight/components/ChartLoading";
import BillingAction from "../../actions/BillingAction";
import LayoutAction from "../../actions/LayoutAction";
import { handleAiContentQuotaError } from "../utils/aiContentQuotaUtils";

interface AIProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (profileData: any) => void;
}

const AIProfileModal: React.FC<AIProfileModalProps> = ({ isOpen, onClose, onSave }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId } = useParams<{ projectId: string }>();

  const isAdmin = useSelector((state: any) => state.userReducer.getIn(["currentUser", "admin"]));
  const industries = useSelector((state: any) => state.ruleReducer.getIn(["industry"])).toJS();
  const project = useSelector(selectProject);
  const aiSetting = useSelector(selectAiSettingPlain);
  const hasAiProfile = useSelector(selectHasValidAiSetting);
  const isEditMode = hasAiProfile;

  const companyName =
    (project?.toJS ? project.toJS() : project)?.name ||
    (project?.toJS ? project.toJS() : project)?.title ||
    t("Your Company");
  const [industry, setIndustry] = useState("Agriculture");
  const [geographicReach, setGeographicReach] = useState("");
  const [coreProducts, setCoreProducts] = useState("");

  const [language, setLanguage] = useState("en");
  const [timezone, setTimezone] = useState("America/New_York");
  const [isLoading, setIsLoading] = useState(false);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);
  const initialSnapshotRef = React.useRef<any>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [platformsInitialized, setPlatformsInitialized] = useState(false);

  const [activeTab, setActiveTab] = useState<string>("profile");
  const [agentSetting, setAgentSetting] = useState({
    isLoadingContentInstructions: false,
    contentInstructions: defaultContentInstructions,
  });

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!industry.trim()) {
      errors.industry = t("Industry is required");
    }
    if (!geographicReach.trim()) {
      errors.geographicReach = t("Geographic reach is required");
    }
    if (!coreProducts.trim()) {
      errors.coreProducts = t("Core products/services is required");
    }

    if (!language.trim()) {
      errors.language = t("Language is required");
    }
    if (!timezone.trim()) {
      errors.timezone = t("TimeZone is required");
    }

    const selectedPlatforms = platforms.filter((p) => p.selected);
    if (selectedPlatforms.length === 0) {
      errors.platforms = t("At least one platform must be selected");
    }

    if (!agentSetting.contentInstructions?.trim()) {
      errors.contentInstructions = t("Content instructions are required");
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const profileData = {
        industry,
        geographicReach,
        coreProducts,
        platforms: platforms.filter((p) => p.selected),
      };

      const aiSettings: any = {
        serviceDescription: coreProducts,
        customerLocation: geographicReach,
        companyIndustry: industry,
        topic: aiSetting?.topic || "",
        postType: aiSetting?.postType || "",
        defaultPhotoUrl: "",
        language: language,
        timezone: timezone,
        contentVariation: "medium",
        startTime: aiSetting?.startTime || "09:00",
        startDate: aiSetting?.startDate || format(new Date(), "yyyy-MM-dd"),
        endDate: aiSetting?.endDate || format(addDays(new Date(), 7), "yyyy-MM-dd"),
        totalDays: aiSetting?.totalDays || 7,
        includeWeekends: aiSetting?.includeWeekends || true,
        facebook: platforms.find((p) => p.id === "facebook")?.selected || false,
        twitter: platforms.find((p) => p.id === "twitter")?.selected || false,
        instagram: platforms.find((p) => p.id === "instagram")?.selected || false,
        linkedin: platforms.find((p) => p.id === "linkedin")?.selected || false,
        threads: platforms.find((p) => p.id === "threads")?.selected || false,
        contentInstructions: agentSetting.contentInstructions.trim(),
      };

      await dispatch(AIContentAction.saveAIContentSetting(projectId, aiSettings));

      onSave(profileData);
      onClose();
    } catch (err: any) {
      console.error("Error saving AI profile:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetDefault = () => {
    resetAIAgent(projectId).then((data) => {
      if (data && data.aiSetting) {
        setAgentSetting((prev) => ({
          ...prev,
          contentInstructions: resolveContentInstructions(data.aiSetting),
        }));
      } else {
        setAgentSetting((prev) => ({
          ...prev,
          contentInstructions: defaultContentInstructions,
        }));
      }
    });
  };

  const normalize = (v: any) => (typeof v === "string" ? v.normalize("NFC").trim() : v);

  const stableStringify = (obj: any) => {
    const keys = Object.keys(obj).sort();
    const ordered: any = {};
    keys.forEach((k) => (ordered[k] = obj[k]));
    return JSON.stringify(ordered);
  };

  const buildSnapshot = React.useCallback(() => {
    return {
      industry: normalize(industry),
      geographicReach: normalize(geographicReach),
      coreProducts: normalize(coreProducts),
      language: normalize(language),
      timezone: normalize(timezone),
    };
  }, [industry, geographicReach, coreProducts, language, timezone]);

  const isDirty = React.useMemo(() => {
    if (!isEditMode) return false;
    if (!initialSnapshotRef.current) return false;
    try {
      const currentStr = stableStringify(buildSnapshot());
      const initialStr = stableStringify(initialSnapshotRef.current);
      return currentStr !== initialStr;
    } catch {
      return false;
    }
  }, [buildSnapshot, isEditMode]);

  const isAgentDataChange = React.useMemo(() => {
    return (agentSetting.contentInstructions || "").trim() !== defaultContentInstructions.trim();
  }, [agentSetting.contentInstructions]);

  const isDisableSave = React.useMemo(() => {
    if (!industry.trim()) {
      return true;
    }
    if (!geographicReach.trim()) {
      return true;
    }
    if (!coreProducts.trim()) {
      return true;
    }

    if (!language.trim()) {
      return true;
    }
    if (!timezone.trim()) {
      return true;
    }

    const selectedPlatforms = platforms.filter((p) => p.selected);
    if (selectedPlatforms.length === 0) {
      return true;
    }

    if (!agentSetting.contentInstructions?.trim()) {
      return true;
    }
    return false;
  }, [
    agentSetting.contentInstructions,
    coreProducts,
    geographicReach,
    industry,
    language,
    platforms,
    timezone,
  ]);

  const handleRequestClose = () => {
    if (!isInitialized) {
      onClose();
      return;
    }
    if (isEditMode && isDirty) {
      setShowUnsavedConfirm(true);
      return;
    }
    onClose();
  };

  React.useEffect(() => {
    if (!isOpen) return;
    if (!aiSetting) return;

    setIsInitialized(false);

    setIndustry(aiSetting.companyIndustry || "");
    setGeographicReach(aiSetting.customerLocation || "");
    setCoreProducts(aiSetting.serviceDescription || "");
    setLanguage(aiSetting.language || "en");
    setTimezone(aiSetting.timezone || "America/New_York");

    setPlatforms((prev) => prev.map((p) => ({ ...p, selected: !!(aiSetting as any)[p.id] })));

    setAgentSetting((prev) => ({
      ...prev,
      contentInstructions: resolveContentInstructions(aiSetting),
    }));

    if (isEditMode) {
      initialSnapshotRef.current = {
        industry: (aiSetting.companyIndustry || "").trim(),
        geographicReach: (aiSetting.customerLocation || "").trim(),
        coreProducts: (aiSetting.serviceDescription || "").trim(),
        language: (aiSetting.language || "en").trim(),
        timezone: (aiSetting.timezone || "America/New_York").trim(),
      };
    } else {
      initialSnapshotRef.current = null;
    }

    setIsInitialized(true);
  }, [isOpen, aiSetting, isEditMode]);

  const handlePlatformsChange = (updatedPlatforms: Platform[]) => {
    setPlatforms(updatedPlatforms);
    if (!platformsInitialized) {
      setPlatformsInitialized(true);
    }
  };

  const handleConnectPlatform = async () => {
    const aiSettings: any = {
      serviceDescription: coreProducts,
      customerLocation: geographicReach,
      companyIndustry: industry,
      topic: aiSetting?.topic || "",
      postType: aiSetting?.postType || "",
      defaultPhotoUrl: "",
      language: language,
      timezone: timezone,
      contentVariation: "medium",
      startTime: aiSetting?.startTime || "09:00",
      startDate: aiSetting?.startDate || format(new Date(), "yyyy-MM-dd"),
      endDate: aiSetting?.endDate || format(addDays(new Date(), 7), "yyyy-MM-dd"),
      totalDays: aiSetting?.totalDays || 7,
      includeWeekends: aiSetting?.includeWeekends || true,
      facebook: platforms.find((p) => p.id === "facebook")?.selected || false,
      twitter: platforms.find((p) => p.id === "twitter")?.selected || false,
      instagram: platforms.find((p) => p.id === "instagram")?.selected || false,
      linkedin: platforms.find((p) => p.id === "linkedin")?.selected || false,
      threads: platforms.find((p) => p.id === "threads")?.selected || false,
      contentInstructions: agentSetting.contentInstructions.trim(),
    };

    await dispatch(AIContentAction.saveAIContentSetting(projectId, aiSettings));
  };

  const handleGenerateContentInstructions = async () => {
    setAgentSetting((prev) => ({
      ...prev,
      isLoadingContentInstructions: true,
    }));

    try {
      const data = await generateContentInstructions(
        projectId!,
        coreProducts,
        geographicReach,
        industry,
        language
      );

      if (data?.content_instructions?.trim()) {
        setAgentSetting((prev) => ({
          ...prev,
          isLoadingContentInstructions: false,
          contentInstructions: data.content_instructions!.trim(),
        }));
      } else {
        setAgentSetting((prev) => ({
          ...prev,
          isLoadingContentInstructions: false,
        }));
      }
      if (projectId) {
        dispatch(BillingAction.getBillingTokenUsage(projectId) as any);
      }
    } catch (err) {
      if (handleAiContentQuotaError(dispatch, err) && projectId) {
        dispatch(BillingAction.getBillingTokenUsage(projectId) as any);
      } else {
        dispatch(
          LayoutAction.setNotification({
            title: t("Content instructions"),
            type: "error",
            message: t("Failed to generate content instructions"),
          }) as any
        );
      }
      setAgentSetting((prev) => ({
        ...prev,
        isLoadingContentInstructions: false,
      }));
    }
  };

  const renderProfile = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-6">
          <div>
            <SelectField
              value={industries.find((item) => item.value === industry)}
              onChange={(opt: any) => {
                setIndustry(opt?.value);
              }}
              options={industries}
              label={t("What industry or sector does your company operate in?")}
            />
            {validationErrors.industry && (
              <p className="text-sm text-red-600 mt-1">{validationErrors.industry}</p>
            )}
          </div>

          <div>
            <InputField
              label={t("What is your company's geographic reach?")}
              value={geographicReach}
              onChange={setGeographicReach}
              placeholder={t("e.g., Global, focusing on North America and Europe")}
            />
            {validationErrors.geographicReach && (
              <p className="text-sm text-red-600 mt-1">{validationErrors.geographicReach}</p>
            )}
          </div>

          <div>
            <InputField
              label={t("Please list your core products and/or services")}
              value={coreProducts}
              onChange={setCoreProducts}
              placeholder={t("e.g., Tiered subscription model (Basic, Pro, Enterprise)")}
            />
            {validationErrors.coreProducts && (
              <p className="text-sm text-red-600 mt-1">{validationErrors.coreProducts}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("Select language for AI content generation")}
            </label>
            <div>
              <SelectField
                value={{
                  value: language,
                  label:
                    LANGUAGE_OPTIONS.find((option) => option.value === language)?.label || language,
                }}
                onChange={(opt: any) => {
                  setLanguage(opt?.value);
                }}
                options={LANGUAGE_OPTIONS}
                placeholder={t("Select language for AI content generation")}
              />
              {validationErrors.language && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.language}</p>
              )}
            </div>
          </div>

          <div>
            <SelectField
              value={TIMEZONE_OPTIONS.find((item) => item.value === timezone)}
              onChange={(opt: any) => {
                setTimezone(opt?.value);
              }}
              options={TIMEZONE_OPTIONS}
              label={t("Select timezone for AI content generation")}
            />
            {validationErrors.timezone && (
              <p className="text-sm text-red-600 mt-1">{validationErrors.timezone}</p>
            )}
          </div>
        </div>
        <div>
          <PlatformSelection
            view="profile"
            onPlatformsChange={handlePlatformsChange}
            onConnectPlatform={handleConnectPlatform}
          />
          {validationErrors.platforms && (
            <p className="text-sm text-red-600 mt-2">{validationErrors.platforms}</p>
          )}
        </div>
      </div>
    );
  };

  const renderAgentSetting = () => {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold">{t("Content instructions")}</div>
              <p className="text-sm text-gray-500 mt-0.5">
                {t(
                  "Describe the task, tone, guidelines, and how summary and description fields should be written. The response structure (one item per date) is fixed by the system."
                )}
              </p>
            </div>
            {isAdmin ? (
              <div
                className="bg-blue-500 flex items-center gap-1 px-1.5 py-0.5 rounded-md cursor-pointer"
                onClick={() =>
                  agentSetting.isLoadingContentInstructions
                    ? null
                    : handleGenerateContentInstructions()
                }
              >
                <Wand className="w-2.5 h-2.5 text-white" />
                <span className="text-xs font-medium text-white">{t("Auto-fill with AI")}</span>
              </div>
            ) : (
              <div></div>
            )}
          </div>
          <div className="relative">
            {agentSetting.isLoadingContentInstructions && (
              <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-3 flex items-center justify-center rounded-lg">
                <ChartLoading label={t("Generating content…")}></ChartLoading>
              </div>
            )}
            <textarea
              readOnly={!isAdmin}
              value={agentSetting.contentInstructions}
              onChange={(e) => {
                setAgentSetting((prev) => ({
                  ...prev,
                  contentInstructions: e.target.value || "",
                }));
              }}
              placeholder={t("Type...")}
              className="w-full h-96 p-2 border border-gray-200 rounded-md outline-none resize-none"
            />
          </div>
          {validationErrors.contentInstructions && (
            <p className="text-sm text-red-600 mt-1">{validationErrors.contentInstructions}</p>
          )}
        </div>
      </div>
    );
  };

  const modalContent = (
    <div className="p-6">
      <div className="w-full flex justify-start gap-3 relative">
        <button
          onClick={() => setActiveTab("profile")}
          className={`flex items-center justify-center gap-2 px-6 py-2 text-sm font-medium z-1 ${
            activeTab === "profile" ? "text-blue-600 border-b-2 border-blue-600" : "text-gray-800"
          }`}
        >
          <Sparkles
            className={`w-4 h-4 ${activeTab === "profile" ? "text-blue-600" : "text-gray-800"}`}
          />
          {t("AI Profile")}
        </button>
        <button
          onClick={() => setActiveTab("agent")}
          className={`flex items-center justify-center gap-2 px-6 py-2 text-sm font-medium z-1 ${
            activeTab === "agent" ? "text-blue-600 border-b-2 border-blue-600" : "text-gray-800"
          }`}
        >
          <BotMessageSquare
            className={`w-4 h-4 ${activeTab === "agent" ? "text-blue-600" : "text-gray-800"}`}
          />
          {t("AI Agent")}
        </button>
        <div className="w-full absolute bottom-0 border border-gray-200"></div>
      </div>
      <div className="pt-4 w-full h-full overflow-y-auto">
        {activeTab === "profile" ? renderProfile() : null}
        {activeTab === "agent" ? renderAgentSetting() : null}
      </div>
    </div>
  );

  const modalFooter = (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {isAdmin && activeTab === "agent" ? (
          <Button
            variant="primary"
            size="md"
            onClick={handleResetDefault}
            disabled={!isAgentDataChange}
          >
            {t("Reset Default")}
          </Button>
        ) : null}
      </div>
      <div className="flex items-center justify-end gap-3">
        <Button variant="secondary" size="md" onClick={handleRequestClose}>
          {t("Cancel")}
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={handleSave}
          disabled={isDisableSave || isLoading}
          leftIcon={
            isLoading ? (
              <div className="w-4 h-4 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 text-white" />
            )
          }
        >
          {isLoading
            ? isEditMode
              ? t("Saving...")
              : t("Creating...")
            : isEditMode
              ? t("Save Changes")
              : t("Create Your AI Profile")}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleRequestClose}
        title={
          isEditMode
            ? t("Edit AI Profile for {{companyName}}", { companyName })
            : t("AI Profile for {{companyName}}", { companyName })
        }
        subtitle={
          isEditMode
            ? t("Update your team profile details for better AI results.")
            : t("Let's create a team profile of yours for our AI model.")
        }
        className="w-3xl max-h-[98vh]"
        footer={modalFooter}
      >
        {modalContent}
      </Modal>

      <ConfirmDialog
        visible={showUnsavedConfirm}
        title={t("Unsaved Changes")}
        description={t("You have unsaved changes. Do you want to discard them?")}
        alertIcon={<TriangleAlertIcon />}
        onSubmit={() => {
          setShowUnsavedConfirm(false);
          onClose();
        }}
        onClose={() => setShowUnsavedConfirm(false)}
      />
    </>
  );
};

export default AIProfileModal;
